import { FC } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faList, faMap } from "@fortawesome/free-solid-svg-icons";
import type { AsyncStatus } from "../../components/types";
import get_gateway_config_icon from "src/assets/images/get_gateway_config_icon.png";
import { ConfigurationStatusIndicator } from "../../general-configuration/components/configuration-status.component";
import type { TimerProfile } from "../../components/types";
import { formatTime } from "../timer-profile.utils";

type TimerProfileDetailCardProps = {
  profile: TimerProfile | null;
  zoneInfoStatus: AsyncStatus;
  onEdit: () => void;
  onDelete: () => void;
  onGetZoneInfo: () => void;
  onViewZoneInfo: () => void;
  onToggleEnabled: (profile: TimerProfile, action: "enable" | "disable") => void;
  enableDisableStatus?: AsyncStatus;
  isLoading?: boolean;
};

export const TimerProfileDetailCard: FC<TimerProfileDetailCardProps> = ({
  profile,
  zoneInfoStatus,
  onEdit,
  onDelete,
  onGetZoneInfo,
  onViewZoneInfo,
  onToggleEnabled,
  enableDisableStatus = "idle",
  isLoading = false,
}) => {
  if (!profile) return null;

  return (
    <section className="profile-detail-card">
      <div className="configuration-card__header configuration-card__header--middle">
        <div>
          <h3 className="configuration-card__title">{profile.profileName}</h3>
          <p className="configuration-card__subtitle">
            Profile ID: {profile.profileId}
          </p>
        </div>
        <div className="configuration-card__header-actions">
          <button
            type="button"
            className="configuration-button configuration-button--compact"
            onClick={onEdit}
          >
            Edit
          </button>
          <button
            type="button"
            className="configuration-button configuration-button--danger configuration-button--compact"
            onClick={onDelete}
          >
            Delete
          </button>
        </div>
      </div>

      <div className="profile-detail-grid">
        <ScheduleSummaryCard
          startTime={profile.startTime}
          endTime={profile.endTime}
          dayOfWeek={profile.daysOfWeekName}
        />
        <IntensitySummaryCard dimLevel={profile.dimLevel} brightLevel={profile.brightLevel} />
        <ZoneSummaryCard
          profile={profile}
          zoneInfoStatus={zoneInfoStatus}
          onGetZoneInfo={onGetZoneInfo}
          onViewZoneInfo={onViewZoneInfo}
        />
        <StatusSummaryCard
          profile={profile}
          onToggleEnabled={onToggleEnabled}
          isToggling={enableDisableStatus === "pending"}
        />
      </div>
    </section>
  );
}

type ScheduleSummaryCardProps = {
  startTime: string;
  endTime: string;
  dayOfWeek: string;
};

function ScheduleSummaryCard({ startTime, endTime, dayOfWeek }: ScheduleSummaryCardProps) {
  return (
    <div className="profile-summary-card">
      <div className="profile-summary-card__title">Schedule</div>
      <div className="profile-summary-card__grid">
        <div>
          <span>Start Time</span>
          <strong>{startTime} ({formatTime(startTime)})</strong>
        </div>
        <div>
          <span>End Time</span>
          <strong>{endTime} ({formatTime(endTime)})</strong>
        </div>
      </div>

      <div className="profile-schedule-divider" />

      <div className="profile-chip-row profile-chip-row--stacked">
        <span className="profile-summary-card__label">Active Days</span>
        <span className="configuration-pill">
          {dayOfWeek}
        </span>
      </div>
    </div>
  );
}

function IntensitySummaryCard({ dimLevel, brightLevel }: { dimLevel: number; brightLevel: number }) {
  return (
    <div className="profile-summary-card">
      <div className="profile-summary-card__title">Light Intensity</div>
      <div className="profile-intensity-meter">
        <IntensityBar label="Dim Level" value={dimLevel} />
        <IntensityBar label="Bright Level" value={brightLevel} />
      </div>
    </div>
  );
}

function IntensityBar({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="profile-intensity-meter__row">
        <span>{label}</span>
        <strong>{value}%</strong>
      </div>
      <div className="sensor-range-card__track">
        <span
          className="sensor-range-card__fill"
          style={{ width: `${value}%` }}
        />
      </div>
    </div>
  );
}

type ZoneSummaryCardProps = {
  profile: TimerProfile;
  zoneInfoStatus: AsyncStatus;
  onGetZoneInfo: () => void;
  onViewZoneInfo: () => void;
};

function ZoneSummaryCard({
  profile,
  zoneInfoStatus,
  onGetZoneInfo,
  onViewZoneInfo,
}: ZoneSummaryCardProps) {
  return (
    <div className="profile-summary-card profile-summary-card--wide">
      <div className="profile-summary-card__header">
        <div className="profile-summary-card__title">Assigned Zone</div>
        <div className="profile-summary-card__header-actions">
          <button
            type="button"
            className="configuration-button configuration-button--compact"
            // onClick={onGetZoneInfo}
            disabled={!profile?.zoneId || zoneInfoStatus === "pending"}
          >
            <img src={get_gateway_config_icon} alt="Get Zone Info icon" />
            <span style={{ marginTop: "4px" }}>Get Zone Info</span>
          </button>
          <ConfigurationStatusIndicator status={zoneInfoStatus} pendingLabel="Pending" />
          <button
            type="button"
            className="gateway-config-view-btn"
            // onClick={onViewZoneInfo}
            title="View zone info"
          >
            <FontAwesomeIcon icon={faList} />
          </button>
        </div>
      </div>
      <div className="profile-zone-card">
        <div className="profile-zone-card__icon">
          <FontAwesomeIcon icon={faMap} />
        </div>
        <div>
          <strong>{profile.group || `Zone ${profile.zoneId}`}</strong>
          <span>Zone {profile.zoneId}</span>
        </div>
      </div>
    </div>
  );
}

type StatusSummaryCardProps = {
  profile: TimerProfile;
  onToggleEnabled: (profile: TimerProfile, action: "enable" | "disable") => void;
  isToggling?: boolean;
};

function StatusSummaryCard({ profile, onToggleEnabled, isToggling = false }: StatusSummaryCardProps) {
  const isActive = profile.status === "Enabled";

  return (
    <div className="profile-summary-card profile-summary-card--status">
      <div className="profile-summary-card__title">
        Configuration Status
      </div>
      <div className="profile-status-row">
        <label className="switch switch--small">
          <input
            type="checkbox"
            checked={isActive}
            disabled={isToggling}
            onChange={() =>
              onToggleEnabled(profile, isActive ? "disable" : "enable")
            }
          />
          <span className="switch__slider" />
        </label>
        <span>Profile Enabled</span>
        <span className="configuration-pill configuration-pill--soft">
          Light Action Mode: {profile.actionName}
        </span>
      </div>
    </div>
  );
}